import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys, useAuthenticatedClient } from './queries'
import type { components } from './schema'

// Who we are on our purchase orders: Bill To, Buyer and our UPS account (USA-1664).
export type Company = components['schemas']['CompanyRead']
export type PurchasingAccount = components['schemas']['PurchasingAccountRead']
export type PurchasingAccountUpdate = components['schemas']['PurchasingAccountUpdate']
export type CompanyBlock = 'bill_to' | 'buyer'

export function useCompany() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.companySettings.all,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/company')
      if (error) throw error
      return data
    },
  })
}

export function useUpdateUpsAccount() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const key = queryKeys.companySettings.all
  return useMutation({
    mutationFn: async (upsAccountNumber: string) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/company', {
        body: { ups_account_number: upsAccountNumber },
      })
      if (error) throw error
      return data
    },
    onMutate: async (upsAccountNumber) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<Company>(key)
      if (previous) {
        queryClient.setQueryData<Company>(key, {
          ...previous,
          ups_account_number: upsAccountNumber,
        })
      }
      return { previous }
    },
    onError: (_error, _number, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    onSuccess: (company) => queryClient.setQueryData(key, company),
  })
}

export function useUpdatePurchasingAccount() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ block, body }: { block: CompanyBlock; body: PurchasingAccountUpdate }) => {
      const client = await getClient()
      const { data, error } =
        block === 'bill_to'
          ? await client.PATCH('/dashboard/company/bill-to', { body })
          : await client.PATCH('/dashboard/company/buyer', { body })
      if (error) throw error
      return data
    },
    onSuccess: (company) => queryClient.setQueryData(queryKeys.companySettings.all, company),
  })
}
